/**
 * JKL Skin Test
 * 
 * Multi-step skincare test wizard for personalized product recommendations.
 * Migrated from Hydrogen SkinTest component.
 * 
 * Features:
 * - 7 steps: Age, Gender, Skin Type, Customer Needs, Routine, Fav Product, Supplements
 * - Auto-advance on option selection (except last step)
 * - Progress bar at the bottom
 * - Calculates score based on age and customer needs
 * - Redirects to appropriate thank you page based on score
 */

(function() {
  'use strict';

  // Point mapping for score calculation
  // Score = needs_points (10-60) + age_points (1-5)
  var POINT_MAPPING = {
    // Age points (1-5)
    'below-25': 1,
    'between-25-35': 2,
    'between-36-45': 3,
    'between-46-55': 4,
    'over-55': 5,
    // Customer needs points (10-60)
    'hydrate': 10,
    'brighten': 20,
    'sun-protection': 30,
    'purify': 40,
    'prevent-first-wrinkles': 50,
    'reduce-wrinkles': 60
  };

  // Auto-advance delay in milliseconds
  var AUTO_ADVANCE_DELAY = 300;

  // Find all skin test sections on the page
  var sections = document.querySelectorAll('[data-section-id]');
  
  sections.forEach(function(section) {
    if (!section.classList.contains('jkl-st')) return;
    
    initSkinTest(section);
  });

  /**
   * Initialize a single skin test instance
   * @param {HTMLElement} section - The section element
   */
  function initSkinTest(section) {
    // Get configuration from JSON
    var configEl = section.querySelector('[data-skin-test-config]');
    if (!configEl) {
      console.error('JKL Skin Test: Configuration not found');
      return;
    }

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('JKL Skin Test: Invalid configuration JSON', e);
      return;
    }

    // Check if user is logged in
    if (!config.customerId) {
      console.log('JKL Skin Test: User not logged in, form not initialized');
      return;
    }

    // State
    var state = {
      currentStep: 0,
      answers: {},
      isSubmitting: false
    };

    // DOM Elements
    var elements = {
      form: section.querySelector('[data-skin-test-form]'),
      questionTitle: section.querySelector('[data-step-question]'),
      steps: section.querySelectorAll('[data-step]'),
      btnBack: section.querySelector('[data-btn-back]'),
      btnNext: section.querySelector('[data-btn-next]'),
      btnSubmit: section.querySelector('[data-btn-submit]'),
      progressDots: section.querySelectorAll('[data-progress-dot]'),
      progressLines: section.querySelectorAll('[data-progress-line]')
    };

    // Initialize event listeners
    initEventListeners();

    /**
     * Initialize all event listeners
     */
    function initEventListeners() {
      // Option selection
      var options = section.querySelectorAll('[data-option]');
      options.forEach(function(option) {
        var input = option.querySelector('input[type="radio"]');
        if (input) {
          input.addEventListener('change', function() {
            handleOptionSelect(option, input);
          });
          
          // Also handle click on the label for better UX
          option.addEventListener('click', function(e) {
            if (e.target !== input) {
              input.checked = true;
              input.dispatchEvent(new Event('change'));
            }
          });
        }
      });

      // Navigation buttons
      if (elements.btnBack) {
        elements.btnBack.addEventListener('click', goBack);
      }
      if (elements.btnNext) {
        elements.btnNext.addEventListener('click', goNext);
      }
      if (elements.btnSubmit) {
        elements.btnSubmit.addEventListener('click', submitForm);
      }

      // Prevent form submission on enter
      if (elements.form) {
        elements.form.addEventListener('submit', function(e) {
          e.preventDefault();
        });
      }
    }

    /**
     * Handle option selection
     * @param {HTMLElement} option - The option element
     * @param {HTMLInputElement} input - The radio input
     */
    function handleOptionSelect(option, input) {
      var stepName = input.name;
      var value = input.value;

      // Store answer
      state.answers[stepName] = value;

      // Update visual selection
      var currentStep = section.querySelector('[data-step="' + stepName + '"]');
      if (currentStep) {
        var allOptions = currentStep.querySelectorAll('[data-option]');
        allOptions.forEach(function(opt) {
          opt.classList.remove('is-selected');
        });
        option.classList.add('is-selected');
      }

      // Update navigation buttons
      updateNavigationButtons();

      // Auto-advance to next step (except on last step)
      var isLastStep = state.currentStep === config.totalSteps - 1;
      if (!isLastStep) {
        setTimeout(function() {
          goNext();
        }, AUTO_ADVANCE_DELAY);
      }
    }

    /**
     * Go to the next step
     */
    function goNext() {
      if (state.currentStep >= config.totalSteps - 1) return;
      
      var currentStepName = config.steps[state.currentStep].name;
      if (!state.answers[currentStepName]) return;

      state.currentStep++;
      showStep(state.currentStep);
    }

    /**
     * Go back to the previous step
     */
    function goBack() {
      if (state.currentStep <= 0) return;

      state.currentStep--;
      showStep(state.currentStep);
    }

    /**
     * Show a specific step
     * @param {number} stepIndex - The step index to show
     */
    function showStep(stepIndex) {
      // Hide all steps
      elements.steps.forEach(function(step) {
        step.classList.add('jkl-st--hidden');
        step.classList.remove('is-active');
      });

      // Show current step
      var stepName = config.steps[stepIndex].name;
      var currentStep = section.querySelector('[data-step="' + stepName + '"]');
      if (currentStep) {
        currentStep.classList.remove('jkl-st--hidden');
        currentStep.classList.add('is-active');
      }

      // Update question title
      if (elements.questionTitle) {
        elements.questionTitle.textContent = config.steps[stepIndex].question;
      }

      // Update progress bar
      updateProgressBar(stepIndex);

      // Update navigation buttons
      updateNavigationButtons();
    }

    /**
     * Update the progress bar
     * @param {number} stepIndex - Current step index
     */
    function updateProgressBar(stepIndex) {
      // Update dots
      elements.progressDots.forEach(function(dot, index) {
        var isCompleted = index < stepIndex;
        var isCurrent = index === stepIndex;
        
        dot.classList.toggle('is-completed', isCompleted);
        dot.classList.toggle('is-active', isCompleted || isCurrent);
        dot.setAttribute('aria-current', isCurrent ? 'step' : '');
      });

      // Update lines
      elements.progressLines.forEach(function(line) {
        var lineIndex = line.dataset.progressLine;
        
        if (lineIndex === 'start') {
          // Start line is always active if we're past step 0
          line.classList.toggle('is-active', stepIndex >= 0);
        } else if (lineIndex === 'end') {
          // End line is active only on last step
          line.classList.toggle('is-active', stepIndex === config.totalSteps - 1);
        } else {
          // Between lines are active if we've passed that step
          var idx = parseInt(lineIndex, 10);
          line.classList.toggle('is-active', idx < stepIndex);
        }
      });
    }

    /**
     * Update navigation buttons visibility
     */
    function updateNavigationButtons() {
      var isFirstStep = state.currentStep === 0;
      var isLastStep = state.currentStep === config.totalSteps - 1;
      var currentStepName = config.steps[state.currentStep].name;
      var hasAnswer = !!state.answers[currentStepName];

      // Back button: show if not first step
      if (elements.btnBack) {
        elements.btnBack.classList.toggle('jkl-st--hidden', isFirstStep);
      }

      // Next button: show if has answer and not last step
      if (elements.btnNext) {
        elements.btnNext.classList.toggle('jkl-st--hidden', !hasAnswer || isLastStep);
      }

      // Submit button: show if has answer and is last step
      if (elements.btnSubmit) {
        elements.btnSubmit.classList.toggle('jkl-st--hidden', !hasAnswer || !isLastStep);
      }
    }

    /**
     * Calculate the score based on answers
     * @returns {number} The calculated score
     */
    function calculateScore() {
      var score = 0;
      
      // Add age points
      if (state.answers['age'] && POINT_MAPPING[state.answers['age']]) {
        score += POINT_MAPPING[state.answers['age']];
      }
      
      // Add customer needs points
      if (state.answers['customer-needs'] && POINT_MAPPING[state.answers['customer-needs']]) {
        score += POINT_MAPPING[state.answers['customer-needs']];
      }
      
      return score;
    }

    /**
     * Get the thank you page URL for a specific score
     * @param {number} score - The calculated score
     * @returns {string} The thank you page URL
     */
    function getThankYouPageUrl(score) {
      var pages = config.thankYouPages;
      var scoreStr = score.toString();
      
      // Determine which group the score belongs to
      var groupKey = null;
      if (score >= 11 && score <= 15) groupKey = 'hydrate_group';
      else if (score >= 21 && score <= 25) groupKey = 'brighten_group';
      else if (score >= 31 && score <= 35) groupKey = 'sun_protection_group';
      else if (score >= 41 && score <= 45) groupKey = 'purify_group';
      else if (score >= 51 && score <= 55) groupKey = 'prevent_wrinkles_group';
      else if (score >= 61 && score <= 65) groupKey = 'reduce_wrinkles_group';
      
      // Try to get specific page for this score
      if (groupKey && pages[groupKey] && pages[groupKey][scoreStr]) {
        var specificPage = pages[groupKey][scoreStr];
        if (specificPage && specificPage !== '') {
          return specificPage;
        }
      }
      
      // Fallback to default page
      return pages.default || '/';
    }

    /**
     * Build translated answers object
     * @returns {Object} Answers with translated labels
     */
    function buildTranslatedAnswers() {
      var translated = {};
      
      Object.keys(state.answers).forEach(function(key) {
        var value = state.answers[key];
        var translatedValue = config.translations[value] || value;
        translated[key] = translatedValue;
      });
      
      return translated;
    }

    /**
     * Submit the form
     */
    function submitForm() {
      if (state.isSubmitting) return;
      
      // Validate all steps have answers
      var allAnswered = config.steps.every(function(step) {
        return !!state.answers[step.name];
      });
      
      if (!allAnswered) {
        console.error('JKL Skin Test: Not all questions answered');
        return;
      }
      
      state.isSubmitting = true;
      
      // Calculate score
      var score = calculateScore();
      
      // Build submission data
      var submissionData = {
        customerId: config.customerId,
        score: score,
        answers: buildTranslatedAnswers()
      };
      
      console.log('JKL Skin Test: Submitting', submissionData);
      
      // Note: In Liquid/Shopify, we don't have a direct API endpoint like in Hydrogen.
      // The thank you page should handle storing the results via a Shopify app or metafields.
      // For now, we just redirect to the thank you page with the score.
      
      var thankYouUrl = getThankYouPageUrl(score);
      var separator = thankYouUrl.indexOf('?') >= 0 ? '&' : '?';
      var redirectUrl = thankYouUrl + separator + 'score=' + score;
      
      // Store answers in sessionStorage for the thank you page to access
      try {
        sessionStorage.setItem('skinTestAnswers', JSON.stringify(submissionData));
      } catch (e) {
        console.warn('JKL Skin Test: Could not store answers in sessionStorage', e);
      }
      
      // Redirect to thank you page
      window.location.href = redirectUrl;
    }
  }
})();
